import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonBack, CustomButton } from "../../../../core/components/CustomButton";
import { TextComponent } from "../../../../core/components/TextComponent";
import { Toast } from "../../../../core/components/Toast";
import CircularProgress from "../../../../core/components/CircularProgress";
import {
  CommunityHelpPost,
  getCommunityPosts,
  submitCommunityPost,
  supportCommunityPost,
} from "../../data/CommunityHelpDataSource";

const kindLabels: Record<string, string> = {
  app_issue: "App issue",
  feature_idea: "Feature idea",
  success_story: "Success story",
  general_feedback: "General feedback",
};

const emptyForm = {
  kind: "app_issue",
  title: "",
  body: "",
  displayName: "",
};

export const CommunityHelpBoardScreen = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [posts, setPosts] = useState<CommunityHelpPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const publicPosts = useMemo(
    () => posts.filter((post) => post.kind === "app_issue" || post.kind === "feature_idea"),
    [posts],
  );
  const stories = useMemo(
    () => posts.filter((post) => post.kind === "success_story"),
    [posts],
  );

  const loadPosts = async () => {
    setLoading(true);
    try {
      setPosts(await getCommunityPosts());
    } catch {
      setMessage("Could not load Community right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPosts();
  }, []);

  const updateForm = (key: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async () => {
    const body = form.body.trim();
    if (!body) {
      setMessage("Please enter a message.");
      return;
    }

    setSaving(true);
    try {
      await submitCommunityPost({
        kind: form.kind,
        title: form.title.trim(),
        body,
        displayName: form.displayName.trim(),
      });
      setForm(emptyForm);
      setMessage("Thanks. Your post is now waiting for review.");
      await loadPosts();
    } catch {
      setMessage("Could not submit this right now.");
    } finally {
      setSaving(false);
    }
  };

  const handleSupport = async (post: CommunityHelpPost) => {
    try {
      const updated = await supportCommunityPost(post.id);
      setPosts((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch {
      setMessage("Could not update this right now.");
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-white px-4 py-5">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
        <ButtonBack onClick={() => navigate(-1)} className="w-fit text-primary">
          Community
        </ButtonBack>

        <section className="rounded-lg border border-grayLight bg-white p-4 shadow-sm">
          <TextComponent variant="h3" className="font-semibold">
            Share with Unjabbed
          </TextComponent>
          <div className="mt-4 grid gap-3">
            <label className="grid gap-1 text-sm font-semibold text-graySecondary">
              Type
              <select
                value={form.kind}
                onChange={(event) => updateForm("kind", event.target.value)}
                className="rounded-md border border-grayLight px-3 py-3 text-black"
              >
                <option value="app_issue">App issue</option>
                <option value="feature_idea">Feature idea</option>
                <option value="success_story">Success story</option>
                <option value="general_feedback">General feedback</option>
              </select>
            </label>
            <label className="grid gap-1 text-sm font-semibold text-graySecondary">
              Title
              <input
                value={form.title}
                maxLength={120}
                onChange={(event) => updateForm("title", event.target.value)}
                className="rounded-md border border-grayLight px-3 py-3 text-black"
              />
            </label>
            <label className="grid gap-1 text-sm font-semibold text-graySecondary">
              What would you like to share?
              <textarea
                value={form.body}
                maxLength={1200}
                rows={5}
                onChange={(event) => updateForm("body", event.target.value)}
                className="resize-y rounded-md border border-grayLight px-3 py-3 text-black"
              />
            </label>
            {form.kind === "success_story" && (
              <label className="grid gap-1 text-sm font-semibold text-graySecondary">
                First name only, optional
                <input
                  value={form.displayName}
                  maxLength={40}
                  onChange={(event) => updateForm("displayName", event.target.value)}
                  className="rounded-md border border-grayLight px-3 py-3 text-black"
                />
              </label>
            )}
            <CustomButton onClick={handleSubmit} loading={saving}>
              {saving ? "Submitting..." : "Submit for review"}
            </CustomButton>
          </div>
        </section>

        <section>
          <TextComponent variant="h3" className="font-semibold">
            Help board
          </TextComponent>
          <div className="mt-3 grid gap-3">
            {loading ? (
              <div className="flex justify-center py-8">
                <CircularProgress />
              </div>
            ) : publicPosts.length === 0 ? (
              <TextComponent variant="h5" className="text-graySecondary">
                No approved community posts yet.
              </TextComponent>
            ) : (
              publicPosts.map((post) => (
                <CommunityPostCard
                  key={post.id}
                  post={post}
                  onSupport={() => handleSupport(post)}
                />
              ))
            )}
          </div>
        </section>

        {stories.length > 0 && (
          <section>
            <TextComponent variant="h3" className="font-semibold">
              Success stories
            </TextComponent>
            <div className="mt-3 grid gap-3">
              {stories.map((post) => (
                <CommunityPostCard key={post.id} post={post} />
              ))}
            </div>
          </section>
        )}
      </div>

      {message && (
        <Toast
          title="Community"
          message={message}
          handleClose={() => setMessage(null)}
        />
      )}
    </div>
  );
};

const CommunityPostCard = ({
  post,
  onSupport,
}: {
  post: CommunityHelpPost;
  onSupport?: () => void;
}) => {
  const supportLabel =
    post.kind === "feature_idea" ? "I'd like this too" : "I'm having this too";

  return (
    <article className="rounded-lg border border-grayLight bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <TextComponent variant="h6" className="font-semibold text-primary">
          {kindLabels[post.kind] || "General feedback"}
        </TextComponent>
        <TextComponent variant="h7" className="text-graySecondary">
          {post.adminStatus}
        </TextComponent>
      </div>
      <TextComponent variant="h4" className="mt-2 font-semibold">
        {post.title}
      </TextComponent>
      <TextComponent variant="h5" className="mt-2 whitespace-pre-wrap text-graySecondary">
        {post.body}
      </TextComponent>
      {post.displayName && (
        <TextComponent variant="h6" className="mt-2 text-graySecondary">
          Shared by {post.displayName}
        </TextComponent>
      )}
      {onSupport && (
        <button
          type="button"
          onClick={onSupport}
          className="mt-4 rounded-md border border-primary px-4 py-2 text-sm font-semibold text-primary hover:bg-socondary"
        >
          {supportLabel} ({post.affectedCount})
        </button>
      )}
    </article>
  );
};
